/**
 * GedcomImportPlanSummary Component
 *
 * Displays a summary of the import plan showing what will be imported.
 * Part of Phase 4 - Slice 6: Data Transformation (Import Plan UI)
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ParseResult } from '../hooks/useGedcomParse';
import { useGedcomImportPlan } from '../hooks/useGedcomImportPlan';

interface GedcomImportPlanSummaryProps {
  parseResult: ParseResult;
}

export function GedcomImportPlanSummary({ parseResult }: GedcomImportPlanSummaryProps) {
  const { t } = useTranslation('common');
  const { state: planState, plan, buildPlan } = useGedcomImportPlan();

  // Auto-build plan when parse result is available
  useEffect(() => {
    if (parseResult && planState === 'idle') {
      buildPlan(parseResult);
    }
  }, [parseResult, planState, buildPlan]);

  // Don't show anything while building
  if (planState === 'building' || planState === 'idle' || !plan) {
    return null;
  }

  // Show error if plan failed to build
  if (planState === 'error') {
    return (
      <div className="p-3 sm:p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <p className="text-xs sm:text-sm text-red-900 dark:text-red-100">
          {t('gedcom.importPlanError', 'Failed to build import plan')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg">
      <h4 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white mb-2">
        {t('gedcom.importReadiness', 'Import Readiness')}
      </h4>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {/* Persons */}
        <div className="p-2 sm:p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mb-0.5">
            {t('gedcom.personsToImport', 'Persons')}
          </p>
          <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
            {plan.import_order.persons_count}
          </p>
        </div>

        {/* Couples */}
        <div className="p-2 sm:p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mb-0.5">
            {t('gedcom.couplesToImport', 'Couples')}
          </p>
          <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
            {plan.import_order.couples_count}
          </p>
        </div>

        {/* Links */}
        <div className="p-2 sm:p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mb-0.5">
            {t('gedcom.linksToImport', 'Links')}
          </p>
          <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
            {plan.import_order.links_count}
          </p>
        </div>

        {/* Facts */}
        <div className="p-2 sm:p-3 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
          <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mb-0.5">
            {t('gedcom.factsToImport', 'Facts')}
          </p>
          <p className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
            {plan.import_order.facts_count}
          </p>
        </div>
      </div>

      {/* Status indicator */}
      <div className="mt-3 flex items-center gap-2">
        {plan.summary.ready_for_import ? (
          <>
            <span className="material-symbols-outlined text-green-600 dark:text-green-400 text-base sm:text-lg">
              check_circle
            </span>
            <p className="text-xs sm:text-sm text-green-900 dark:text-green-100">
              {t('gedcom.readyForImport', 'Ready for import')}
            </p>
          </>
        ) : (
          <>
            <span className="material-symbols-outlined text-red-600 dark:text-red-400 text-base sm:text-lg">
              error
            </span>
            <p className="text-xs sm:text-sm text-red-900 dark:text-red-100">
              {t('gedcom.blockedFromImport', 'Import blocked - fix errors first')}
            </p>
          </>
        )}
      </div>

      {plan.summary.warning_count > 0 && (
        <p className="mt-2 text-[10px] sm:text-xs text-amber-700 dark:text-amber-300">
          {t('gedcom.warningsPresent', {
            count: plan.summary.warning_count,
            defaultValue: '{{count}} warnings to review',
          })}
        </p>
      )}
    </div>
  );
}
